import { z } from 'zod';

/** Schema for a serialized node (as stored in normalized table, returned by API) */
export const CanvasNodeTableSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  pageId: z.string(),
  type: z.string(),
  positionX: z.number(),
  positionY: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  zIndex: z.number().int().default(0),
  parentId: z.string().nullable().optional(),
  data: z.record(z.string(), z.unknown()).default({}),
  style: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/** Schema for creating a node via API */
export const CanvasNodeCreateSchema = z.object({
  id: z.string().max(500),
  type: z.string().max(500),
  position: z.object({ x: z.number(), y: z.number() }),
  data: z.record(z.string(), z.unknown()).optional().default({}),
  style: z.record(z.string(), z.unknown()).optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  zIndex: z.number().int().optional().default(0),
  parentId: z.string().max(500).nullable().optional(),
});

/** Schema for batch position updates */
export const CanvasNodePositionBatchSchema = z.object({
  positions: z
    .array(
      z.object({
        nodeId: z.string().max(500),
        x: z.number(),
        y: z.number(),
      }),
    )
    .min(1)
    .max(5000),
});

/** Schema for updating a single node */
export const CanvasNodeUpdateSchema = z.object({
  data: z.record(z.string(), z.unknown()).optional(),
  style: z.record(z.string(), z.unknown()).optional(),
  zIndex: z.number().int().optional(),
  parentId: z.string().max(500).nullable().optional(),
  type: z.string().max(500).optional(),
  position: z.object({ x: z.number(), y: z.number() }).optional(),
  width: z.number().optional(),
  height: z.number().optional(),
});

/** Schema for a serialized edge (as stored in normalized table, returned by API) */
export const CanvasEdgeTableSchema = z.object({
  id: z.string(),
  edgeId: z.string(),
  pageId: z.string(),
  source: z.string(),
  target: z.string(),
  sourceHandle: z.string().nullable().optional(),
  targetHandle: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  data: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/** Schema for creating an edge via API */
export const CanvasEdgeCreateSchema = z.object({
  id: z.string().max(500),
  source: z.string().max(500),
  target: z.string().max(500),
  sourceHandle: z.string().max(500).nullable().optional(),
  targetHandle: z.string().max(500).nullable().optional(),
  type: z.string().max(500).optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});

/** Schema for full sync (fallback) */
export const CanvasSyncSchema = z.object({
  nodes: z.array(CanvasNodeCreateSchema).max(5000),
  edges: z.array(CanvasEdgeCreateSchema).max(5000),
});

export type CanvasNodeTable = z.infer<typeof CanvasNodeTableSchema>;
export type CanvasNodeCreate = z.infer<typeof CanvasNodeCreateSchema>;
export type CanvasNodePositionBatch = z.infer<typeof CanvasNodePositionBatchSchema>;
export type CanvasNodeUpdate = z.infer<typeof CanvasNodeUpdateSchema>;
export type CanvasEdgeTable = z.infer<typeof CanvasEdgeTableSchema>;
export type CanvasEdgeCreate = z.infer<typeof CanvasEdgeCreateSchema>;
export type CanvasSync = z.infer<typeof CanvasSyncSchema>;
